/**
 * @fileoverview Main application component that sets up routing, theme, and internationalization.
 * Routes are protected using DmsPermissions with the pattern:
 * - <FEATURE>_PAGE: For page access (sidebar + route + API GET)
 * - <FEATURE>_<ACTION>: For specific actions (button + API POST/PUT/DELETE)
 */

import React, { useEffect, useContext, useState } from "react";
import { Route, Navigate } from "react-router-dom";

import {
  DmsAccessDenied,
  DmsPageNotFound,
  DmsRouter,
  DmsUnderDevelopment,
  ROUTES,
  ToastContainer,
  DmsIntlManager,
  LanguageBaseContext,
  LOCALES as LOCALES_BASE,
  DmsPermissions,
  useToken,
} from "dmsbasereact";

import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { appTheme } from "./theme/appTheme";
import { DmsAlertPage, ROUTES as ALERT_ROUTES, LOCALES as LOCALES_MODAPP } from "dmsalertsystemreact";
import { APP_ROUTES } from "./utils/NamedRoutes"
import { Permissions } from "./constants/Permissions";

import { Layout } from "./pages/Layout/Layout";
import { LOCALES as LOCALES_APP } from "./constants/Locales";

import { DmsReturnPage } from "./pages/DmsReturnPage/DmsReturnPage";
import { DmsProductionPage } from "./pages/DmsProductionPage/DmsProductionPage";
import { DmsPageTrainingVideo } from "./pages/TrainingVideo/DmsPageTrainingVideo";
import { DmsDispensationHistoryPage } from "./pages/DmsDispensationHistoryPage/DmsDispensationHistoryPage";
import { DispensationCancellationRequestsPage } from "./pages/DispensationCancellationRequestsPage";
import { DispensationPage } from "./pages/DispensationPage/DispensationPage";
import { DispensationInsightsPage } from "./pages/DispensationInsights/DispensationInsightsPage";

import { PatientPage } from "./pages/Patient/PatientPage";
import { SystemSettings } from "./pages/SystemSettings/SystemSettings";
import { CheckinCheckoutPage } from "./pages/CheckinCheckoutPage/CheckinCheckoutPage";
import { CheckinPage } from "./pages/CheckinPage/CheckinPage";
import { CheckoutPage } from "./pages/CheckoutPage/CheckoutPage";
import { TrainingGroupPage } from "./pages/TrainingGroupPage/TrainingGroupPage";
import { TrainingPage } from "./pages/TrainingPage/TrainingPage";
import { InstitutionalGroupPage } from "./pages/InstitutionalGroupPage/InstitutionalGroupPage";
import { TrainingMaterialPage } from "./pages/TrainingMaterialPage/TrainingMaterialPage";
import { TrainingCentralPage } from "./pages/TrainingCentralPage/TrainingCentralPage";
import { MyTrainingsPage } from "./pages/MyTrainingsPage/MyTrainingsPage";
import { CompletedTrainingsPage } from "./pages/CompletedTrainingsPage/CompletedTrainingsPage";
import { EmittedCertificatesPage } from "./pages/EmittedCertificatesPage/EmittedCertificatesPage";

// Import pages removed - imports are now done via CLI script (backend/scripts/import_data.py)
import { SearchPage } from "./pages/Search/SearchPage";
import { HomePage } from "./pages/Home/HomePage";
import { LoginPage } from "./pages/Login/LoginPage";
import { MFAVerificationPage } from "./pages/Login/MFAVerificationPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage/ResetPasswordPage";
import { RegisterPage } from "./pages/RegisterPage/RegisterPage";
import { SsoLoginPage } from "./pages/SsoLogin/SsoLoginPage";
import { AdvancedAuditPage } from "./pages/AdvancedAuditPage/AdvancedAuditPage";
import { DataManagementPage } from "./pages/DataManagementPage/DataManagementPage";
import { MyPatientsPage } from "./pages/MyPatientsPage/MyPatientsPage";
import { DirectoryApprovePage } from "./pages/DirectoryApprovePage/DirectoryApprovePage";
import { AboutPage } from "./pages/AboutPage/AboutPage";
import { ProfileSettingsPage } from "./pages/ProfileSettingsPage/ProfileSettingsPage";
import { PrescriptionHistoryPage } from "./pages/PrescriptionHistoryPage/PrescriptionHistoryPage";

import { CreateAccountPage } from "./pages/CreateAccountPage/CreateAccountPage";
import { UserManagementPage } from "./pages/UserManagementPage";
import { SAPCommunicationPage } from "./pages/SAPCommunicationPage/SAPCommunicationPage";
import { SapIntegrationPage } from "./pages/SapIntegrationPage";
import { SAPHomologatedInstitutionsPage } from "./pages/SAPHomologatedInstitutionsPage/SAPHomologatedInstitutionsPage";
import { PendingAccountApprovalsPage } from "./pages/PendingAccountApprovalsPage";
import { InstitutionCentralPage } from "./pages/InstitutionCentralPage";
import { InstitutionStockPage } from "./pages/InstitutionStockPage";
import { StockReconciliationPage } from "./pages/StockReconciliationPage";
import { StockManagementPage } from "./pages/StockManagementPage";
import { RolesPage } from "./pages/RolesPage";
import { DirectorGroupsPage } from "./pages/DirectorGroupsPage/DirectorGroupsPage";
import { SystemNoticePage, SystemNoticeDetailPage } from "./pages/SystemNoticePage";
import { InstitutionPartnerGroupsPage } from "./pages/InstitutionPartnerGroupsPage";
import { InstitutionManagementPage } from "./pages/InstitutionManagementPage";
import { PartnerPage } from "./pages/PartnerPage";
import { PartnerV2Page, PartnerV2DetailPage } from "./pages/PartnerV2Page";
import { PartnerBranchPage, PartnerBranchDetailPage } from "./pages/PartnerBranchPage";
import { ReturnQualityPage } from "./pages/ReturnQualityPage";
import { ReturnManagementPage } from "./pages/ReturnManagementPage";
import { PrescriptorSettingsPage } from "./pages/PrescriptorSettingsPage";
import { ContentSettings } from "./pages/ContentSettings/ContentSettings";
import { SalesAnalyticsPage, TraceabilityAnalyticsPage, ProductionAnalyticsPage } from "./pages/AnalyticsPage";
import {
  PrescriptionsReportPage,
  DispensationsReportPage,
  PatientsReportPage,
  InstitutionsReportPage,
  DoctorsReportPage,
  PharmacistsReportPage,
  PrescriptionsDispensationsReportPage,
  HomologatedClientsReportPage,
  PrescriptionDispensationOverviewPage,
} from "./pages/ReportsPage";
import MyDownloadsPage from "./services/AsyncExport/MyDownloadsPage/MyDownloadsPage";

export const App = () => {
  DmsIntlManager.additionalLocales(LOCALES_BASE);
  DmsIntlManager.additionalLocales(LOCALES_MODAPP);
  DmsIntlManager.additionalLocales(LOCALES_APP);

  const { language, setLanguage } = useContext(LanguageBaseContext);
  const [, setForcePage] = useState(0);
  const { token } = useToken();

  useEffect(() => {
    const updateLanguage = async () => {
      await DmsIntlManager.updateLanguage(language, setLanguage);
      setForcePage((prev) => prev + 1);
    };
    updateLanguage();
  }, [language, setLanguage]);

  const publicContent = (
    <>
      <Route path={ROUTES.Login} element={
        !token ? <LoginPage /> : <Navigate to={ROUTES.Home} />
      } />
      <Route path={APP_ROUTES.Register} element={
        !token ? <RegisterPage /> : <Navigate to={ROUTES.Home} />
      } />
      <Route path={APP_ROUTES.MFAVerification} element={
        !token ? <MFAVerificationPage /> : <Navigate to={ROUTES.Home} />
      } />
      <Route path={APP_ROUTES.ForgotPassword} element={
        !token ? <ForgotPasswordPage /> : <Navigate to={ROUTES.Home} />
      } />
      <Route path={APP_ROUTES.ResetPassword} element={
        !token ? <ResetPasswordPage /> : <Navigate to={ROUTES.Home} />
      } />
      <Route path={APP_ROUTES.SsoLogin} element={
        !token ? <SsoLoginPage /> : <Navigate to={ROUTES.Home} />
      } />
      <Route path={APP_ROUTES.SsoCallback} element={
        !token ? <SsoLoginPage /> : <Navigate to={ROUTES.Home} />
      } />
      <Route path={APP_ROUTES.CreateAccount} element={
        !token ? <CreateAccountPage /> : <Navigate to={ROUTES.Home} />
      } />
    </>
  );

  const privateContent = (
    <Route path="/" element={<Layout />}>
      {/* Home - accessible to all authenticated users */}
      <Route index path={ROUTES.Home} element={<HomePage />} />

      {/* Doctor pages */}
      <Route path={APP_ROUTES.MyPatients} element={
        <DmsPermissions permissions={[Permissions.MY_PATIENTS_PAGE]} fallback={<DmsAccessDenied />}>
          <MyPatientsPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.PrescriptionHistory} element={
        <DmsPermissions permissions={[Permissions.PRESCRIPTION_HISTORY_PAGE]} fallback={<DmsAccessDenied />}>
          <PrescriptionHistoryPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.MyPrescriptionDispensation} element={
        <DmsPermissions permissions={[Permissions.PRESCRIPTION_DISPENSATION_OVERVIEW_PAGE]} fallback={<DmsAccessDenied />}>
          <PrescriptionDispensationOverviewPage />
        </DmsPermissions>
      } />

      {/* Pharmacist pages */}
      <Route path={APP_ROUTES.Dispensing} element={
        <DmsPermissions permissions={[Permissions.DISPENSATION_PAGE]} fallback={<DmsAccessDenied />}>
          <DispensationPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.DispensingHistory} element={
        <DmsPermissions permissions={[Permissions.DISPENSATION_HISTORY_PAGE]} fallback={<DmsAccessDenied />}>
          <DmsDispensationHistoryPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.DispensationCancellationRequests} element={
        <DmsPermissions permissions={[Permissions.DISPENSATION_CANCELLATION_APPROVE]} fallback={<DmsAccessDenied />}>
          <DispensationCancellationRequestsPage />
        </DmsPermissions>
      } />

      {/* Return page */}
      <Route path={APP_ROUTES.Return} element={
        <DmsPermissions permissions={[Permissions.RETURN_PAGE]} fallback={<DmsAccessDenied />}>
          <DmsReturnPage />
        </DmsPermissions>
      } />

      {/* Return Quality Review page (Eurofarma Garantia) */}
      <Route path={APP_ROUTES.ReturnQuality} element={
        <DmsPermissions permissions={[Permissions.RETURN_QUALITY_PAGE]} fallback={<DmsAccessDenied />}>
          <ReturnQualityPage />
        </DmsPermissions>
      } />

      {/* Return Management page (Gestão Lena) */}
      <Route path={APP_ROUTES.ReturnManagement} element={
        <DmsPermissions permissions={[Permissions.RETURN_MANAGEMENT_PAGE]} fallback={<DmsAccessDenied />}>
          <ReturnManagementPage />
        </DmsPermissions>
      } />

      {/* Patient page */}
      <Route path={APP_ROUTES.Patient} element={
        <DmsPermissions permissions={[Permissions.PATIENT_PAGE]} fallback={<DmsAccessDenied />}>
          <PatientPage />
        </DmsPermissions>
      } />

      {/* Institution Responsible pages */}
      <Route path={APP_ROUTES.InstitutionCentral} element={
        <DmsPermissions permissions={[Permissions.MY_INSTITUTION_PAGE]} fallback={<DmsAccessDenied />}>
          <InstitutionCentralPage />
        </DmsPermissions>
      } />

      {/* Institution approval page (admin only) */}
      <Route path={APP_ROUTES.InstitutionApproval} element={
        <DmsPermissions permissions={[Permissions.INSTITUTION_APPROVE]} fallback={<DmsAccessDenied />}>
          <DirectoryApprovePage />
        </DmsPermissions>
      } />

      {/* Stock pages (shared) */}
      <Route path={APP_ROUTES.InstitutionStock} element={
        <DmsPermissions permissions={[Permissions.STOCK_OVERVIEW_PAGE]} fallback={<DmsAccessDenied />}>
          <InstitutionStockPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.StockReconciliation} element={
        <DmsPermissions permissions={[Permissions.STOCK_RECONCILE]} fallback={<DmsAccessDenied />}>
          <StockReconciliationPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.StockManagement} element={
        <DmsPermissions permissions={[Permissions.STOCK_MANAGEMENT_PAGE]} fallback={<DmsAccessDenied />}>
          <StockManagementPage />
        </DmsPermissions>
      } />

      {/* Check-in / Check-out legacy route */}
      <Route path={APP_ROUTES.CheckinCheckout} element={
          <CheckinCheckoutPage />
      } />

      <Route path={APP_ROUTES.Checkin} element={
        <DmsPermissions permissions={[Permissions.STOCK_CHECKIN]} fallback={<DmsAccessDenied />}>
          <CheckinPage />
        </DmsPermissions>
      } />

      <Route path={APP_ROUTES.Checkout} element={
        <DmsPermissions permissions={[Permissions.STOCK_CHECKOUT]} fallback={<DmsAccessDenied />}>
          <CheckoutPage />
        </DmsPermissions>
      } />

      {/* Analytics page (shared) - Legacy Insights route */}
      <Route path={APP_ROUTES.Insights} element={
        <DmsPermissions permissions={[Permissions.ANALYTICS_PAGE]} fallback={<DmsAccessDenied />}>
          <DispensationInsightsPage />
        </DmsPermissions>
      } />

      {/* Analytics pages - Sales and Traceability */}
      <Route path={APP_ROUTES.Analytics} element={
        <Navigate to={APP_ROUTES.AnalyticsSales} replace />
      } />
      <Route path={APP_ROUTES.AnalyticsSales} element={
        <DmsPermissions permissions={[Permissions.ANALYTICS_PAGE]} fallback={<DmsAccessDenied />}>
          <SalesAnalyticsPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.AnalyticsTraceability} element={
        <DmsPermissions permissions={[Permissions.ANALYTICS_PAGE]} fallback={<DmsAccessDenied />}>
          <TraceabilityAnalyticsPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.AnalyticsProduction} element={
        <DmsPermissions permissions={[Permissions.ANALYTICS_PAGE]} fallback={<DmsAccessDenied />}>
          <ProductionAnalyticsPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.ReportsPrescriptionDispensation} element={
        <DmsPermissions permissions={[Permissions.REPORTS_PAGE]} fallback={<DmsAccessDenied />}>
          <PrescriptionDispensationOverviewPage />
        </DmsPermissions>
      } />

      {/* Reports pages - Management reports */}
      <Route path={APP_ROUTES.ReportsPrescriptions} element={
        <DmsPermissions permissions={[Permissions.REPORTS_PAGE]} fallback={<DmsAccessDenied />}>
          <PrescriptionsReportPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.ReportsDispensations} element={
        <DmsPermissions permissions={[Permissions.REPORTS_PAGE]} fallback={<DmsAccessDenied />}>
          <DispensationsReportPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.ReportsPatients} element={
        <DmsPermissions permissions={[Permissions.REPORTS_PAGE]} fallback={<DmsAccessDenied />}>
          <PatientsReportPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.ReportsInstitutions} element={
        <DmsPermissions permissions={[Permissions.REPORTS_PAGE]} fallback={<DmsAccessDenied />}>
          <InstitutionsReportPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.ReportsDoctors} element={
        <DmsPermissions permissions={[Permissions.REPORTS_PAGE]} fallback={<DmsAccessDenied />}>
          <DoctorsReportPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.ReportsPharmacists} element={
        <DmsPermissions permissions={[Permissions.REPORTS_PAGE]} fallback={<DmsAccessDenied />}>
          <PharmacistsReportPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.ReportsPrescriptionsDispensations} element={
        <DmsPermissions permissions={[Permissions.REPORTS_PAGE]} fallback={<DmsAccessDenied />}>
          <PrescriptionsDispensationsReportPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.ReportsDirectChannelClients} element={
        <DmsPermissions permissions={[Permissions.REPORTS_PAGE]} fallback={<DmsAccessDenied />}>
          <HomologatedClientsReportPage />
        </DmsPermissions>
      } />

      {/* Admin pages */}
      <Route path={ROUTES.SystemSettings} element={
        <DmsPermissions permissions={[Permissions.SYSTEM_SETTINGS_PAGE, Permissions.CAN_MANAGE_GENERAL_SETTINGS, Permissions.CAN_MANAGE_SAP_SETTINGS, Permissions.CAN_MANAGE_API_KEYS, Permissions.CAN_MANAGE_SMTP_SETTINGS, Permissions.CAN_MANAGE_SSO_SETTINGS, Permissions.CAN_MANAGE_LOGIN_SETTINGS, Permissions.CAN_MANAGE_SECURITY_SETTINGS]} all_needed={false} fallback={<DmsAccessDenied />}>
          <SystemSettings />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.AdvancedAudit} element={
        <DmsPermissions permissions={[Permissions.ADVANCED_AUDIT_PAGE]} fallback={<DmsAccessDenied />}>
          <AdvancedAuditPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.DataManagement} element={
        <DmsPermissions permissions={[Permissions.DATA_MANAGEMENT_PAGE]} fallback={<DmsAccessDenied />}>
          <DataManagementPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.UserManagement} element={
        <DmsPermissions permissions={[Permissions.USER_MANAGEMENT_PAGE]} fallback={<DmsAccessDenied />}>
          <UserManagementPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.PendingAccountApprovals} element={
        <DmsPermissions permissions={[Permissions.PENDING_APPROVALS_PAGE]} fallback={<DmsAccessDenied />}>
          <PendingAccountApprovalsPage />
        </DmsPermissions>
      } />
      <Route path={ROUTES.Role} element={
        <DmsPermissions permissions={[Permissions.ROLES_PAGE]} fallback={<DmsAccessDenied />}>
          <RolesPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.DirectorGroups} element={
        <DmsPermissions permissions={[Permissions.DIRECTOR_GROUPS_PAGE]} fallback={<DmsAccessDenied />}>
          <DirectorGroupsPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.SystemNotice} element={
        <DmsPermissions permissions={[Permissions.NOTICE_PAGE]} fallback={<DmsAccessDenied />}>
          <SystemNoticePage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.SystemNoticeDetail} element={
        <DmsPermissions permissions={[Permissions.NOTICE_PAGE]} fallback={<DmsAccessDenied />}>
          <SystemNoticeDetailPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.MyDownloads} element={<MyDownloadsPage />} />
      <Route path={APP_ROUTES.InstitutionPartnerGroups} element={
        <DmsPermissions permissions={[Permissions.INSTITUTION_PARTNER_GROUPS_PAGE]} fallback={<DmsAccessDenied />}>
          <InstitutionPartnerGroupsPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.InstitutionManagement} element={
        <DmsPermissions permissions={[Permissions.INSTITUTION_MANAGEMENT_PAGE]} fallback={<DmsAccessDenied />}>
          <InstitutionManagementPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.Partner} element={
        <DmsPermissions permissions={[Permissions.PARTNER_LINK_CREATE]} fallback={<DmsAccessDenied />}>
          <PartnerPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.ReportsDistributorClients} element={
        <DmsPermissions permissions={[Permissions.PARTNER_PAGE]} fallback={<DmsAccessDenied />}>
          <PartnerV2Page />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.ReportsDistributorClientsDetail} element={
        <DmsPermissions permissions={[Permissions.PARTNER_PAGE]} fallback={<DmsAccessDenied />}>
          <PartnerV2DetailPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.PartnerBranch} element={
        <DmsPermissions permissions={[Permissions.BRANCH_PAGE]} fallback={<DmsAccessDenied />}>
          <PartnerBranchPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.PartnerBranchDetail} element={
        <DmsPermissions permissions={[Permissions.BRANCH_PAGE]} fallback={<DmsAccessDenied />}>
          <PartnerBranchDetailPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.PrescriptorSettings} element={
        <DmsPermissions permissions={[Permissions.SETTINGS_GENERAL]} fallback={<DmsAccessDenied />}>
          <PrescriptorSettingsPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.ContentSettings} element={
        <DmsPermissions permissions={[Permissions.CAN_MANAGE_HELP_CENTER, Permissions.CAN_MANAGE_GENERAL_SETTINGS]} all_needed={false} fallback={<DmsAccessDenied />}>
          <ContentSettings />
        </DmsPermissions>
      } />
      <Route path={ROUTES.Product} element={
        <DmsPermissions permissions={[Permissions.SYSTEM_SETTINGS_PAGE]} fallback={<DmsAccessDenied />}>
          <DmsProductionPage />
        </DmsPermissions>
      } />

      {/* SAP Integration pages */}
      <Route path={APP_ROUTES.SapCommunication} element={
        <DmsPermissions permissions={[Permissions.SAP_INTEGRATION_PAGE]} fallback={<DmsAccessDenied />}>
          <SAPCommunicationPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.TraceabilitySap} element={
        <DmsPermissions permissions={[Permissions.SAP_INTEGRATION_PAGE]} fallback={<DmsAccessDenied />}>
          <SapIntegrationPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.SapHomologatedInstitutions} element={
        <DmsPermissions permissions={[Permissions.SAP_HOMOLOGATED_INSTITUTIONS_PAGE]} fallback={<DmsAccessDenied />}>
          <SAPHomologatedInstitutionsPage />
        </DmsPermissions>
      } />

      {/* Training pages */}
      <Route path={APP_ROUTES.TrainingCentral} element={
        <DmsPermissions permissions={[Permissions.TRAINING_CENTRAL_PAGE]} fallback={<DmsAccessDenied />}>
          <TrainingCentralPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.TrainingGroup} element={
        <DmsPermissions permissions={[Permissions.TRAINING_CENTRAL_PAGE]} fallback={<DmsAccessDenied />}>
          <TrainingGroupPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.Training} element={
        <DmsPermissions permissions={[Permissions.TRAINING_CENTRAL_PAGE]} fallback={<DmsAccessDenied />}>
          <TrainingPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.InstitutionalGroup} element={
        <DmsPermissions permissions={[Permissions.TRAINING_CENTRAL_PAGE]} fallback={<DmsAccessDenied />}>
          <InstitutionalGroupPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.TrainingMaterial} element={
        <DmsPermissions permissions={[Permissions.EUROFARMA_GESTAO_LENA]} fallback={<DmsAccessDenied />}>
          <TrainingMaterialPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.MyTrainings} element={
        <MyTrainingsPage />
      } />
      <Route path={APP_ROUTES.CompletedTrainings} element={
        <DmsPermissions permissions={[Permissions.COMPLETED_TRAININGS_PAGE]} fallback={<DmsAccessDenied />}>
          <CompletedTrainingsPage />
        </DmsPermissions>
      } />
      <Route path={APP_ROUTES.EmittedCertificates} element={
        <DmsPermissions permissions={[Permissions.EMITTED_CERTIFICATES_PAGE]} fallback={<DmsAccessDenied />}>
          <EmittedCertificatesPage />
        </DmsPermissions>
      } />

      {/* Training video - accessible to external users */}
      <Route path={APP_ROUTES.TrainingVideo} element={<DmsPageTrainingVideo />} />
      <Route path={APP_ROUTES.TrainingVideoDetail} element={<DmsPageTrainingVideo />} />

      {/* Alert system pages */}
      <Route path={ALERT_ROUTES.DmsAlertPage} element={
        <DmsPermissions permissions={[Permissions.ALERT_SYSTEM_PAGE]} fallback={<DmsAccessDenied />}>
          <DmsAlertPage />
        </DmsPermissions>
      } />
      {/* <Route path={ALERT_ROUTES.DmsActiveAlertPage} element={
        <DmsPermissions permissions={[Permissions.ALERT_SYSTEM_PAGE]} fallback={<DmsAccessDenied />}>
          <DmsActiveAlertPage />
        </DmsPermissions>
      } /> */}

      {/* Import pages removed - imports are now done via CLI script */}

      {/* General pages accessible to all authenticated users */}
      <Route path={APP_ROUTES.About} element={<AboutPage />} />
      <Route path={APP_ROUTES.ProfileSettings} element={<ProfileSettingsPage />} />
      <Route path={APP_ROUTES.Search} element={<SearchPage />} />

      {/* Error pages */}
      <Route path={ROUTES.UnderDevelopment} element={<DmsUnderDevelopment />} />
      <Route path={ROUTES.NotFound} element={<DmsPageNotFound />} />
      <Route path="*" element={<DmsPageNotFound />} />
    </Route>
  );

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <DmsRouter
        privateContent={privateContent}
        publicContent={publicContent}
      />
      <ToastContainer />
    </ThemeProvider>
  );
};
